import { useRef, useEffect } from 'react';
import { 
  ArrowDownCircleIcon, 
  ArrowUpCircleIcon, 
  BanknotesIcon,
  CreditCardIcon, 
  ArrowPathIcon
} from '@heroicons/react/24/outline';

/**
 * TypeSelector Component
 * 
 * Displays a dropdown menu to select transaction types
 */
const TypeSelector = ({ 
  transactionId, 
  currentType = 'expense',
  onSelectType, 
  onCancel 
}) => {
  const ref = useRef(null);
  
  // Handle outside clicks to close the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onCancel();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onCancel]);
  
  const handleSelect = (type) => {
    onSelectType(transactionId, type);
  };
  
  return (
    <div 
      className="z-40 bg-white shadow-lg rounded-lg overflow-hidden w-40 border border-gray-100 animate-fadeIn"
      ref={ref}
      style={{ 
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)'
      }}
    >
      <style jsx="true">{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out forwards;
        }
      `}</style>
      <div className="overflow-y-auto py-2" role="menu" aria-orientation="vertical">
        <button
          className={`flex items-center w-full px-3 py-2 text-left text-sm transition-colors duration-150 
                    ${currentType === 'income' 
                      ? 'bg-green-100 text-green-700 font-medium' 
                      : 'text-gray-700 hover:bg-green-50 hover:text-green-600 bg-white'}`}
          role="menuitem"
          onClick={() => handleSelect('income')}
        >
          <div className="rounded-full bg-green-100 p-1 mr-2 flex-shrink-0">
            <ArrowDownCircleIcon className="h-3 w-3 text-green-600" />
          </div>
          <span>Income</span>
        </button>
        
        <button
          className={`flex items-center w-full px-3 py-2 text-left text-sm transition-colors duration-150 
                    ${currentType === 'expense' 
                      ? 'bg-red-100 text-red-700 font-medium' 
                      : 'text-gray-700 hover:bg-red-50 hover:text-red-600 bg-white'}`}
          role="menuitem"
          onClick={() => handleSelect('expense')}
        >
          <div className="rounded-full bg-red-100 p-1 mr-2 flex-shrink-0">
            <ArrowUpCircleIcon className="h-3 w-3 text-red-600" />
          </div>
          <span>Expense</span>
        </button>
        
        <button
          className={`flex items-center w-full px-3 py-2 text-left text-sm transition-colors duration-150 
                    ${currentType === 'bills' 
                      ? 'bg-amber-100 text-amber-700 font-medium' 
                      : 'text-gray-700 hover:bg-amber-50 hover:text-amber-600 bg-white'}`}
          role="menuitem"
          onClick={() => handleSelect('bills')}
        >
          <div className="rounded-full bg-amber-100 p-1 mr-2 flex-shrink-0">
            <BanknotesIcon className="h-3 w-3 text-amber-600" />
          </div>
          <span>Bills</span>
        </button>
        
        <button
          className={`flex items-center w-full px-3 py-2 text-left text-sm transition-colors duration-150 
                    ${currentType === 'payment' 
                      ? 'bg-purple-100 text-purple-700 font-medium' 
                      : 'text-gray-700 hover:bg-purple-50 hover:text-purple-600 bg-white'}`}
          role="menuitem"
          onClick={() => handleSelect('payment')}
        >
          <div className="rounded-full bg-purple-100 p-1 mr-2 flex-shrink-0">
            <CreditCardIcon className="h-3 w-3 text-purple-600" />
          </div>
          <span>Payment</span>
        </button>
        
        <button
          className={`flex items-center w-full px-3 py-2 text-left text-sm transition-colors duration-150 
                    ${currentType === 'transfer' 
                      ? 'bg-blue-100 text-blue-700 font-medium' 
                      : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600 bg-white'}`}
          role="menuitem"
          onClick={() => handleSelect('transfer')}
        >
          <div className="rounded-full bg-blue-100 p-1 mr-2 flex-shrink-0">
            <ArrowPathIcon className="h-3 w-3 text-blue-600" />
          </div>
          <span>Transfer</span>
        </button>
      </div>
    </div>
  );
};

export default TypeSelector; 